import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/library/card";
import DetailItem from "../DetailItem";
import InlineEditableDetail from "../InlineEdit/InlineEditableDetail";
import { useInlineFieldEdit } from "../InlineEdit/useInlineFieldEdit";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { DEFAULT_COUNTRY_CODE } from "@/constants/countryCodes";
import { formatLeadPhone } from "@/features/leads/utilities/formatLeadPhone";
import {
  formatLeadWhatsapp,
  joinWhatsapp,
  splitStoredWhatsapp,
} from "@/features/leads/utilities/formatLeadWhatsapp";
import useBranches from "@/features/users/api/useBranches";
import { useViewLeadFieldVisibility } from "@/features/leads/hook/useViewLeadFieldVisibility";
import { useRequiredFieldMark } from "@/features/leads/requirements/requiredFieldMarks";
import { useAllowCreatedAtEdit } from "@/features/leads/api/useLeadFormConfig";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import {
  composeCreatedAt,
  formatCreatedAtDay,
  toLocalDayIso,
  todayLocalDayIso,
} from "@/features/leads/utils/leadCreatedAt";
import { useLeadViewFieldOrder } from "@/features/leads/hook/useLeadViewFieldOrder";

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

export default function BasicDetailsViewMode({
  embedded,
}: {
  embedded?: boolean;
}) {
  const { watch } = useLeadForm();

  const { phone, phoneCountryCode, email, whatsapp, gender, location, branchId } =
    watch() as any;

  // Render the phone as `+91 9876543210` — the same rule the list / grid /
  // board cards use (`formatLeadPhone`), so the drawer and the card agree.
  const displayPhone = formatLeadPhone(phone, phoneCountryCode);
  // WhatsApp with its code too (owner, 2026-10-08). ONE stored string: a
  // value with its own "+cc" is split; the code is never borrowed from the
  // phone (owner, 2026-10-25) — see formatLeadWhatsapp.ts for the exact rule.
  const displayWhatsapp = formatLeadWhatsapp(whatsapp);
  const splitWhatsapp = splitStoredWhatsapp(whatsapp);

  // Resolve branch name from the cached branches list rather than asking
  // the server to re-include the relation on every lead-detail load.
  // `branchSelects` is the same React-Query cache the header picker /
  // assign drawer already populate, so this is free in practice.
  const { branchSelects } = useBranches();
  const branchIdStr = branchId != null ? String(branchId) : "";
  const branchName = branchIdStr
    ? (branchSelects.find((b: { value: string }) => b.value === branchIdStr)?.name ?? "")
    : "";

  const { canInlineEdit, lockReason, saveLeadFields } = useInlineFieldEdit();
  const { isFieldVisible } = useViewLeadFieldVisibility();

  // Stage-requirement stars. Keys are the `Fields` requirement's
  // `referenceKey`, i.e. the Leads column name — "Mobile" is `phone`.
  const requiredPhone = useRequiredFieldMark("field", "phone");
  const requiredEmail = useRequiredFieldMark("field", "email");
  const requiredWhatsapp = useRequiredFieldMark("field", "whatsapp");
  const requiredLocation = useRequiredFieldMark("field", "location");

  // Org-wide "Add/Edit Created At" provision: surfaces the lead's created
  // date here, inline-editable by day. Read from the lead (not the form) so
  // it reflects the committed value; the picker keeps the original
  // time-of-day and the server refuses future dates.
  const allowCreatedAtEdit = useAllowCreatedAtEdit();
  const { lead } = useActiveDataMutation();
  const leadCreatedAt = (lead as any)?.createdAt as string | undefined;

  // Same tenant-wide order the edit mode's grips arrange (keys shared), so a
  // drag there is what everyone sees here. Hidden fields are dropped first.
  const basicOrder = useLeadViewFieldOrder("basic");
  const cells = basicOrder.sortByOrder(
    [
      {
        key: "mobile",
        show: isFieldVisible("basic.mobile"),
        node: (
          <InlineEditableDetail
            label="Mobile"
            required={requiredPhone}
            value={displayPhone}
            readOnly={!canInlineEdit}
            lockedReason={lockReason("basic:mobile")}
            editor={{
              kind: "phone",
              value: {
                countryCode: phoneCountryCode || DEFAULT_COUNTRY_CODE,
                national: String(phone ?? ""),
              },
            }}
            onSave={(next: { countryCode: string; national: string }) =>
              saveLeadFields({
                phone: next.national,
                phoneCountryCode: next.countryCode,
              })
            }
          />
        ),
      },
      {
        key: "email",
        show: isFieldVisible("basic.email"),
        node: (
          <InlineEditableDetail
            label="Email"
            required={requiredEmail}
            value={email}
            readOnly={!canInlineEdit}
            lockedReason={lockReason("basic:email")}
            editor={{
              kind: "text",
              type: "email",
              value: email ?? "",
              placeholder: "Add email",
            }}
            onSave={(next: string) => saveLeadFields({ email: next.trim() })}
          />
        ),
      },
      {
        key: "whatsapp",
        show: isFieldVisible("basic.whatsapp"),
        node: (
          <InlineEditableDetail
            label="WhatsApp"
            required={requiredWhatsapp}
            value={displayWhatsapp}
            readOnly={!canInlineEdit}
            lockedReason={lockReason("basic:whatsapp")}
            editor={{
              kind: "phone",
              value: {
                // The WhatsApp number's own code — never the phone's.
                countryCode: splitWhatsapp?.countryCode || DEFAULT_COUNTRY_CODE,
                national: splitWhatsapp?.national ?? "",
              },
              placeholder: "WhatsApp number",
            }}
            onSave={(next: { countryCode: string; national: string }) =>
              saveLeadFields({
                whatsapp: joinWhatsapp(next.countryCode, next.national),
              })
            }
          />
        ),
      },
      {
        key: "gender",
        show: isFieldVisible("basic.gender"),
        node: (
          <InlineEditableDetail
            label="Gender"
            value={gender}
            readOnly={!canInlineEdit}
            lockedReason={lockReason("basic:gender")}
            editor={{
              kind: "select",
              value: gender ?? "",
              options: GENDER_OPTIONS,
            }}
            onSave={(next: string) => saveLeadFields({ gender: next })}
          />
        ),
      },
      {
        key: "location",
        show: isFieldVisible("basic.location"),
        node: (
          <InlineEditableDetail
            label="Location"
            required={requiredLocation}
            value={location}
            readOnly={!canInlineEdit}
            lockedReason={lockReason("basic:location")}
            editor={{
              kind: "text",
              value: location ?? "",
              placeholder: "Add location",
            }}
            onSave={(next: string) => saveLeadFields({ location: next.trim() })}
          />
        ),
      },
      {
        // Branch is deliberately NOT inline-editable: a branch move runs
        // through `/branch-transfer` (assignment revocation, scope re-eval),
        // not the field PATCH — which strips `branchId` anyway.
        key: "branch",
        show: isFieldVisible("basic.branch"),
        node: <DetailItem label="Branch" value={branchName} />,
      },
      {
        key: "createdAt",
        show: allowCreatedAtEdit,
        node: (
          <InlineEditableDetail
            label="Created At"
            value={formatCreatedAtDay(leadCreatedAt)}
            readOnly={!canInlineEdit}
            editor={{
              kind: "date",
              value: toLocalDayIso(leadCreatedAt),
              maxDate: todayLocalDayIso(),
            }}
            onSave={(day: string) =>
              saveLeadFields({ createdAt: composeCreatedAt(day, leadCreatedAt) })
            }
          />
        ),
      },
    ].filter((c) => c.show),
    (c) => c.key,
  );

  const content = (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-4 lg:gap-x-6 pl-1">
      {cells.map((c) => (
        <div key={c.key} className="min-w-0">
          {c.node}
        </div>
      ))}
    </div>
  );

  if (embedded) {
    return <div className="px-3 pt-0.5 pb-2">{content}</div>;
  }

  return (
    <Card className="border-[#e6e8e7] rounded-[16px] gap-1 py-4 shadow-none">
      <CardHeader className="px-4 pb-0">
        <CardTitle className="text-[16px] font-medium text-black tracking-[-0.32px] leading-normal">
          Basic details
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pt-0">{content}</CardContent>
    </Card>
  );
}
